import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import getInTouchSchema from '@propertypal/shared/src/validation/schemas/getInTouchSchema';
import Button from '@propertypal/web-ui/src/buttons/Button';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import TextArea from '@propertypal/web-ui/src/inputs/TextArea';
import TextInput from '@propertypal/web-ui/src/inputs/TextInput';
import Recaptcha, { getCaptchaKey } from '@propertypal/web-ui/src/security/Recaptcha';
import { openToast } from '@propertypal/web-ui/src/toast/ToastContext';
import { SubHeading, Text } from '@propertypal/web-ui/src/typography';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Container, Wrapper, FormWrapper, InputWrapper, SentContainer, ContentWrapper, Form } from './GetInTouch.style';

const GetInTouch: React.FC = () => {
  const [sent, setSent] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const dispatch = useDispatch();

  const handleSubmit = async (values: any) => {
    setSending(true);

    try {
      const recaptcha = await getCaptchaKey('enquire');

      const body = {
        company: values.company,
        name: values.name,
        email: values.email.trim(),
        telephone: values.number,
        comments: values.comments,
        recaptcha,
        reason: 'I want a new website built for my company',
      };

      await axios(
        {
          method: 'POST',
          url: `${API_URL}/agent-enquire`,
          data: body,
        },
        dispatch,
      );

      setSent(true);
    } catch {
      openToast({
        title: 'Error',
        message: "Something went wrong. We couldn't send your enquiry. Please try again!",
        type: 'error',
      });

      setSending(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      company: '',
      name: '',
      number: '',
      email: '',
      comments: '',
    },
    validationSchema: getInTouchSchema,
    onSubmit: handleSubmit,
  });

  return (
    <div id="getInTouch">
      <Recaptcha />

      <Container>
        <Wrapper>
          {sent ? (
            <SentContainer
              id="contact"
              data-testid="website-sent"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
            >
              <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: 46 }} />
              <SubHeading textTransform="uppercase" mt={20} fontSize={28} fontWeight="600">
                Thanks!
              </SubHeading>
              <Text mt={20} align="center" fontSize={18}>
                Your enquiry has been sent, and a member of the team will be in touch shortly
              </Text>
            </SentContainer>
          ) : (
            <>
              <Form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                  <InputWrapper>
                    <TextInput
                      name="company"
                      placeholder="Company Name"
                      onValueChange={formik.handleChange('company')}
                      value={formik.values.company}
                      error={formik.touched.company && formik.errors.company}
                    />
                  </InputWrapper>
                  <InputWrapper>
                    <TextInput
                      name="name"
                      placeholder="Your Name"
                      onValueChange={formik.handleChange('name')}
                      value={formik.values.name}
                      error={formik.touched.name && formik.errors.name}
                    />
                  </InputWrapper>
                  <InputWrapper>
                    <TextInput
                      name="number"
                      placeholder="Your Telephone number"
                      onValueChange={formik.handleChange('number')}
                      value={formik.values.number}
                      error={formik.touched.number && formik.errors.number}
                    />
                  </InputWrapper>
                  <InputWrapper>
                    <TextInput
                      name="email"
                      placeholder="Your Email Address"
                      onValueChange={formik.handleChange('email')}
                      value={formik.values.email}
                      error={formik.touched.email && formik.errors.email}
                    />
                  </InputWrapper>
                  <InputWrapper>
                    <TextArea
                      name="comments"
                      placeholder="Comments"
                      value={formik.values.comments}
                      onValueChange={formik.handleChange('comments')}
                      rows={3}
                      error={formik.touched.comments && formik.errors.comments}
                    />
                  </InputWrapper>
                </FormWrapper>

                <Button type="submit" loading={sending}>
                  Let&apos;s chat
                </Button>
              </Form>
              <ContentWrapper>
                <SubHeading fontSize={60} mb={50}>
                  Ready to take the next step?
                </SubHeading>
                <Text fontSize={24} maxWidth={450}>
                  Contact us about your project and our team will be in touch.
                </Text>
              </ContentWrapper>
            </>
          )}
        </Wrapper>
      </Container>
    </div>
  );
};

export default GetInTouch;
